/**
 * `grade_track_program_preferences`(DB 원본) → 편제·프로그램 페이지용 mode/sync/hoursAdjust LS 사본 재기록.
 *
 * 엄격 모드: LS 사본은 DB 값의 거울이다. 학년 항목을 서버 값으로 "교체"하고, 서버에 없는 편제·학년은 "설정 없음"으로 둔다.
 * (병합하면 편제 및 기준 시수에서 의도적으로 지운 설정이 로컬 사본에서 되살아난다)
 */

import type { GradeTrackProgramPreferences } from '../services/sectionLayoutService';
import { loadGradeTrackProgramPreferencesCache } from '../services/sectionLayoutService';
import { loadTrackHoursAdjustSettingsMap, saveTrackHoursAdjustSettingsMap } from './trackHoursTermScopeStorage';
import { loadTrackProgramModeMap, saveTrackProgramModeMap } from './trackProgramModeStorage';
import { loadTrackProgramSplitMap, saveTrackProgramSplitMap } from './trackProgramSplitStorage';
import { loadTrackProgramSyncRepMap, saveTrackProgramSyncRepMap } from './trackProgramSyncRepStorage';

function stableStringify(value: unknown): string {
  if (value == null || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

function sameMap(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/** @returns LS 사본이 실제로 바뀌었으면 true */
export function applyGradeTrackProgramPrefsToLocalStores(
  workspaceId: string,
  gradeId: string,
  prefs: GradeTrackProgramPreferences
): boolean {
  if (!workspaceId.trim() || !gradeId.trim()) return false;
  let touched = false;

  const modeMap = prefs.modes[gradeId] ?? {};
  if (!sameMap(loadTrackProgramModeMap(workspaceId, gradeId), modeMap)) {
    saveTrackProgramModeMap(workspaceId, gradeId, { ...modeMap });
    touched = true;
  }
  /** v2 모드 맵이 비면 로더가 v1(boolean) 맵으로 폴백하므로, 옛 v1 값이 되살아나지 않게 함께 비운다 */
  if (Object.keys(loadTrackProgramSplitMap(workspaceId, gradeId)).length > 0) {
    saveTrackProgramSplitMap(workspaceId, gradeId, {});
    touched = true;
  }

  const syncReps = prefs.syncReps[gradeId] ?? {};
  if (!sameMap(loadTrackProgramSyncRepMap(workspaceId, gradeId), syncReps)) {
    saveTrackProgramSyncRepMap(workspaceId, gradeId, { ...syncReps });
    touched = true;
  }

  const hoursAdjust = prefs.hoursAdjust[gradeId] ?? {};
  if (!sameMap(loadTrackHoursAdjustSettingsMap(workspaceId, gradeId), hoursAdjust)) {
    saveTrackHoursAdjustSettingsMap(workspaceId, gradeId, { ...hoursAdjust });
    touched = true;
  }

  return touched;
}

const MIRROR_KEY_PREFIXES = [
  'cp.trackProgramMode.v2.',
  'cp.trackProgramSplit.v1.',
  'cp.trackProgramSyncRep.v1.',
  'cp.trackHoursAdjustSettings.v2.',
  'cp.trackHoursTermScope.v1.',
];

/** LS 사본이 남아 있는 학년 id (서버 prefs에서 사라진 학년도 비우기 위해) */
function listMirroredGradeIds(workspaceId: string): string[] {
  if (typeof window === 'undefined') return [];
  const out = new Set<string>();
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key) continue;
      for (const prefix of MIRROR_KEY_PREFIXES) {
        const head = `${prefix}${workspaceId}.`;
        if (!key.startsWith(head)) continue;
        const gradeId = key.slice(head.length).trim();
        if (gradeId) out.add(gradeId);
      }
    }
  } catch {
    /* ignore */
  }
  return Array.from(out);
}

/** prefs 스냅샷(DB 거울)으로 모든 학년의 mode/sync/hoursAdjust LS를 엄격 재기록한다. */
export function reseedGradeTrackProgramPrefsLocalStores(
  workspaceId: string,
  prefsOverride?: GradeTrackProgramPreferences | null
): boolean {
  const prefs = prefsOverride ?? loadGradeTrackProgramPreferencesCache(workspaceId);
  if (!prefs) return false;
  const gradeIds = new Set<string>([
    ...Object.keys(prefs.modes ?? {}),
    ...Object.keys(prefs.syncReps ?? {}),
    ...Object.keys(prefs.hoursAdjust ?? {}),
    ...listMirroredGradeIds(workspaceId),
  ]);
  let touched = false;
  gradeIds.forEach((gradeId) => {
    if (applyGradeTrackProgramPrefsToLocalStores(workspaceId, gradeId, prefs)) touched = true;
  });
  return touched;
}
